import { useReadableMembershipInfo } from '@components/TownPageLayout/useReadableMembershipInfo'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { SpaceAddressFromSpaceId, useGetSingleSubscription } from 'use-towns-client'
import { formatMembershipDate } from 'utils/formatDates'
import { Address, zeroAddress } from 'viem'

import { useMyMembershipStatus } from './useMyMembershipStatus'

export function useRenewCountdownData(args: { displayWindowDays?: number; spaceId: string }) {
    const { displayWindowDays, spaceId } = args
    const { data: membershipStatus } = useMyMembershipStatus({ spaceId })
    const { data: membershipInfo } = useReadableMembershipInfo(spaceId || '')

    const calculatedDisplayWindowDays =
        displayWindowDays ?? calculateDisplayWindowDays(membershipInfo?.duration)

    return toCountdownData({
        isMember: membershipStatus?.isMember,
        isExpired: membershipStatus?.isExpired,
        expiryTime: membershipStatus?.expiryTime,
        displayWindowDays: calculatedDisplayWindowDays,
        membershipDuration: membershipInfo?.duration,
    })
}

// the days before the membership expires that we want to show the countdown
function calculateDisplayWindowDays(membershipDuration?: number): number | undefined {
    if (!membershipDuration) {
        return
    }

    const TWO_DAYS = 2 * 24 * 60 * 60
    const WEEK = 7 * 24 * 60 * 60
    const MONTH = 30 * 24 * 60 * 60

    if (membershipDuration <= TWO_DAYS) {
        return 1
    } else if (membershipDuration <= WEEK) {
        return 3
    } else if (membershipDuration <= MONTH) {
        return 10
    } else {
        return 30
    }
}

export function toCountdownData(args: {
    isMember: boolean | undefined
    isExpired: boolean | undefined
    expiryTime: bigint | undefined
    displayWindowDays: number | undefined
    membershipDuration?: number
}) {
    const { isMember, isExpired, expiryTime, displayWindowDays, membershipDuration } = args
    const now = Math.floor(Date.now() / 1000) // current unix timestamp

    // Handle infinite duration case (expiryTime === 0n)
    const isInfiniteDuration = expiryTime === 0n

    const timeRemaining = isInfiniteDuration ? Infinity : expiryTime ? Number(expiryTime) - now : 0
    const daysRemaining = isInfiniteDuration
        ? Infinity
        : isExpired
          ? 0
          : Math.max(0, Math.ceil(timeRemaining / (24 * 60 * 60)))
    const shouldDisplay =
        displayWindowDays &&
        ((isMember && isExpired) ||
            (isMember && !isInfiniteDuration && daysRemaining <= displayWindowDays))

    return {
        isLoading: isMember === undefined,
        isExpired: !!isExpired && !isInfiniteDuration,
        expiryTime: expiryTime ? Number(expiryTime) : 0,
        timeRemaining,
        daysRemaining,
        shouldDisplay: !!shouldDisplay,
        displayWindow: displayWindowDays,
        isInfiniteDuration,
        membershipDuration,
        expiryDate: expiryTime
            ? formatMembershipDate(new Date(Number(expiryTime) * 1000))
            : undefined,
    }
}

export function useShouldShowRenewMembership(args: { spaceId: string }) {
    const { spaceId } = args
    const { data: myAddress } = useMyAbstractAccountAddress()
    const { data: membershipStatus } = useMyMembershipStatus({ spaceId })
    const { isLoading, isExpired, shouldDisplay } = useRenewCountdownData({
        spaceId,
    })

    // Only check subscription if we have valid data
    const shouldCheckSubscription = spaceId && myAddress && membershipStatus?.tokenId

    // Check if user has an active subscription
    const { data: subscription, isLoading: isLoadingSubscription } = useGetSingleSubscription({
        walletAddress: shouldCheckSubscription ? myAddress : '',
        spaceAddress: shouldCheckSubscription
            ? (SpaceAddressFromSpaceId(spaceId) as Address)
            : zeroAddress,
        tokenId: shouldCheckSubscription ? membershipStatus.tokenId : '',
    })

    if (isLoadingSubscription) {
        return false
    }

    // Don't show renewal if user has an active subscription
    if (subscription?.active) {
        return false
    }

    return !isLoading && (isExpired === true || shouldDisplay)
}
